import { Alert } from 'antd';
import { useEffect, useState } from 'react';

import type { WafStatus } from '../../../src/types.ts';

interface Props {
  status: WafStatus | null;
}

/**
 * Shows a live banner while etravel's WAF is rate-limiting the backend, with a
 * countdown to the next retry. `nextRetryAt` comes from the server; the countdown
 * ticks locally so it stays smooth between polls.
 */
export function WafBanner({ status }: Props) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!status?.blocked) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [status?.blocked]);

  if (!status?.blocked) return null;

  const httpStatus = status.lastStatus ?? 429;
  const remainMs = status.nextRetryAt ? Date.parse(status.nextRetryAt) - now : 0;
  const secs = Math.max(0, Math.ceil(remainMs / 1000));

  const when = status.nextRetryAt ? (secs > 0 ? `Retrying in ${secs}s…` : 'Retrying now…') : 'Backing off…';
  const seenTimes = status.totalBlocks === 1 ? '1 block' : `${status.totalBlocks} blocks`;

  return (
    <Alert
      type="warning"
      showIcon
      banner
      style={{ marginBottom: 16 }}
      message={`Rate limited by etravel's firewall (HTTP ${httpStatus})`}
      description={`${when} Consider switching Tailscale exit node if this keeps happening. (${seenTimes} this session)`}
    />
  );
}
